import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { format } from 'svelte-i18n';

import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';

import { condition_to_character } from 'src/helpers/handle_manage_old_scenes';
import { getPresenceZoneName } from '../../SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/presence-helpers';
import { getCameraZoneName } from '../../SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';
import { extractCameraObjectsParams } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-objects-helpers';
import {
	getCameraObjectLabel,
	getCameraObjectLabelProps,
} from 'src/components/Camera/helpers/camera-objects';
import { real_id } from 'src/helpers/interface_helpers';
import { capFirstChar } from 'src/helpers/utilities';
import {
	VENTORA_FAN_SOURCE_I18N,
	getVentoraFanSourceIcon,
} from 'src/components/Scenes/helpers/ventora_fan_scenes_helpers';

export const equal_to_label_list = (cond_name, value, translated: Record<string, any> = {}) => {
	const _format = get(format);
	switch (cond_name) {
		case 'turn':
		case 'state':
		case 'turn_add_on':
		case 'fan_on':
			const isOn = value === 'on';
			const isOff = value === 'off';

			return {
				name: 'turn state',
				icon: isOff
					? 'fa fa-toggle-off'
					: isOn
						? 'fa-duotone fa-light fa-toggle-on'
						: 'fa fa-toggle-on',
				...(isOn && {
					iconStyle:
						'--fa-primary-color: var(--color-success); --fa-secondary-color: var(--color-success-10); --fa-secondary-opacity: 1',
				}),
				...(isOff && {
					iconColor: '--color-danger',
				}),
				value: _format(`scene_action_to_label_${value}`),
			};
		case 'roller state':
		case 'multi_cover_state':
			return {
				name: cond_name,
				icon:
					value === 'open' || value === 'opening'
						? 'fa fa-blinds-raised'
						: value === 'closed' || value === 'closing'
							? 'fa fa-blinds'
							: value === 'stopped'
								? 'fa fa-hand'
								: false,
				iconColor: '--color-white',
				value: value === 'open' ? _format(`state_opened`) : _format(`state_${value}`),
			};
		case 'roller direction':
			return {
				name: 'roller_cover_state',
				icon: 'fa fa-arrow-up-arrow-down', //value.match(/open/) ? 'fa fa-blinds-rised' : 'fa fa-blinds',
				value: _format(`when_was_${value}`),
			};
		case 'occupancy':
			return {
				name: 'occupancy',
				value: _format(`label_${value}`),
				icon: value === 'occupied' ? 'fa fa-garage-car' : 'fa fa-garage-empty',
			};
		case 'input_value':
		case 'digital_input_add_on': {
			let labelValue = _format(`action_to_label_${value}`);
			// Add input number when device has multiple inputs (e.g. Dimmer 2, Dimmer Gen3)
			if (translated?.input_index !== undefined) {
				labelValue = `${_format('collapse_title_input_state_by_channel', {
					values: { n: translated.input_index + 1 },
				})}: ${capFirstChar(_format(value))}`;
			}
			return {
				name: 'input',
				icon: value === 'on' ? 'fa fa-light-switch-on' : 'fa fa-light-switch-off',
				iconColor:
					value === 'off' ? '--color-danger' : value === 'on' ? '--color-success' : null,
				value: labelValue,
			};
		}
		case 'push_type':
		case 'push_type_add_on': {
			let labelValue = _format(`prop_${value}`);
			// Add button number if available
			if (translated?.input_index !== undefined) {
				const buttonNum = translated.input_index + 1;
				labelValue += ` ${_format('collapse_title_push_type_by_channel', { values: { n: buttonNum } })}`;
			}
			// Add channel information if available
			if (translated?.remote_control_channel && translated.remote_control_channel !== 'any') {
				const channelNum = Number(translated.remote_control_channel.split('_')[1]) + 1;
				// Try direct translation first, fallback to simple text
				try {
					const channelText = _format('label_channel', {
						values: { channel: channelNum },
					});
					labelValue += ` ${channelText}`;
				} catch (error) {
					// Fallback to simple text if translation fails
					labelValue += ` (Channel ${channelNum})`;
				}
			}
			return {
				name: 'push value',
				value: labelValue,
			};
		}
		case 'scroll_position': {
			let labelValue =
				value === 'L'
					? _format('scroll_up', { default: 'Up' })
					: _format('scroll_down', { default: 'Down' });
			let icon = value === 'L' ? 'fas fa-arrow-up-arrow-up' : 'fas fa-arrow-down-arrow-down';
			// Add channel information if available
			if (translated?.remote_control_channel && translated.remote_control_channel !== 'any') {
				const channelNum = Number(translated.remote_control_channel.split('_')[1]) + 1;
				labelValue += ` ${_format('label_channel', { values: { channel: channelNum } })}`;
			}
			return {
				name: 'scroll position',
				icon,
				value: labelValue,
			};
		}
		case 'door':
		case 'window':
			return {
				name: 'door state',
				icon: value === 'open' ? 'fa fa-door-open text-primary' : 'fa fa-door-closed',
				value: _format(`door_${value}`),
			};
		case 'luxury':
			return {
				name: 'illumination',
				icon: 'fa fa-sun',
				value: _format(`alarm_state_${value}`),
			};
		case 'ext_switch':
			return {
				name: 'external switch',
				icon: value === 'on' ? 'fa fa-light-switch-on' : 'fa-light-switch-off',
				iconColor:
					value === 'off' ? '--color-danger' : value === 'on' ? '--color-success' : null,
				value: _format(value),
			};
		case 'motion':
		case 'pir':
			return {
				name: cond_name,
				value: _format(value === 'detected' ? 'when_detected' : 'when_not_detected'),
				icon: value === 'detected' ? 'fas fa-person-walking text-primary' : 'fa fa-person',
			};
		case 'titl':
			return {
				name: cond_name,
				value: _format(value),
				icon: 'fa fa-angle',
			};
		case 'smoke':
			return {
				name: cond_name,
				value: _format(value === 'detected' ? 'when_detected' : 'when_not_detected'),
				icon:
					value === 'detected'
						? 'fa-solid fa-fire-smoke text-danger'
						: 'fak fa-light-fire-smoke-slash-tr',
			};
		case 'flood':
			return {
				name: cond_name,
				value: _format(value === 'detected' ? 'when_detected' : 'when_not_detected'),
				icon:
					value === 'detected'
						? 'fas fa-house-flood-water text-danger'
						: 'fas fa-droplet-slash',
			};
		case 'moisture_alarm':
			return {
				name: cond_name,
				value: _format(value === 'detected' ? 'when_detected' : 'when_not_detected'),
				icon: value === 'detected' ? 'fas fa-droplet' : 'fas fa-droplet-slash',
			};
		case 'gas':
			return {
				name: cond_name,
				value: _format(value ? 'when_detected' : 'when_not_detected'),
				icon: value ? 'fa-solid fa-smoke text-danger' : 'fak fa-light-smoke-slash',
			};
		case 'vibration':
			return {
				name: cond_name,
				icon: 'fa fa-waveform',
				value: _format(value === 'detected' ? 'when_detected' : 'when_not_detected'),
			};
		case 'valve_open_state':
			return {
				name: cond_name,
				icon: 'fa-light fa-pipe-valve',
				value: _format(value === 'open' ? 'alarm_state_state_open' : 'alarm_state_state_closed'),
			};
		case 'reported_by':
			return {
				name: cond_name,
				icon: '',
				value: _format('when_observer_device', {
					values: { device: get(devices)[value]?.name },
					default: 'observer: ' + get(devices)[value]?.name,
				}),
			};
		case 'tariff':
			return {
				name: cond_name,
				icon: 'fa fa-tags',
				value,
			};
		case 'bolt_state':
			return {
				name: cond_name,
				icon:
					value === 'night_lock'
						? 'fa fa-lock'
						: value === 'day_lock'
							? 'fa fa-unlock'
							: 'fa fa-door-open',
				iconColor:
					value === 'night_lock'
						? '--color-danger'
						: value === 'day_lock'
							? '--color-success'
							: '--color-primary',
				value: _format(`condition_operator_bolt_state_${value}`, {
					default: value,
				}),
			};
		case 'camera_motion':
			return {
				name: cond_name,
				value: _format(value === 'true' ? 'option_camera_motion' : 'option_camera_motion_end', {
					default: value === 'true' ? 'Motion detected' : 'Motion stopped',
				}),
				icon: value === 'true' ? 'fas fa-person-walking text-primary' : 'fas fa-person',
			};
		case 'camera_privacy':
			return {
				name: cond_name,
				value: _format(value === 'true' ? 'camera_privacy_mode_on' : 'camera_privacy_mode_off', {
					default: value === 'true' ? 'Privacy On' : 'Privacy Off',
				}),
				icon: value === 'true' ? 'fas fa-video-slash' : 'fas fa-video',
			};
		case 'camera_arm':
			return {
				name: cond_name,
				value: _format(value === 'true' ? 'camera_armed' : 'camera_disarmed', {
					default: value === 'true' ? 'Armed' : 'Disarmed',
				}),
				icon: value === 'true' ? 'fas fa-shield' : 'fas fa-shield-slash',
			};
		case 'camera_objects': {
			const { type, nobjs, minscore } = extractCameraObjectsParams(translated?.transform);
			const object = getCameraObjectLabel(type);
			const label = _format('label_camera_objects', {
				values: { count: nobjs, object, confidence: minscore },
				default: `${nobjs} ${object} (${minscore}%)`,
			});
			return {
				name: cond_name,
				...getCameraObjectLabelProps(type, label),
			};
		}
		case 'camera_zone_motion': {
			const zoneName = translated?.add_info_ind
				? getCameraZoneName(translated.add_info_ind, readDeviceSettings(translated?.device?.id))
				: _format('camera_zone', { default: 'zone' });
			return {
				name: cond_name,
				value:
					value === 'true'
						? _format('label_camera_zone_motion', {
								values: { zone: zoneName },
								default: `Motion (${zoneName})`,
							})
						: _format('label_camera_zone_no_motion', {
								values: { zone: zoneName },
								default: `No motion (${zoneName})`,
							}),
				icon: value === 'true' ? 'fas fa-person-walking text-primary' : 'fas fa-person',
			};
		}
		case 'fan_source': {
			const sourceI18nKey = VENTORA_FAN_SOURCE_I18N[value];
			return {
				name: cond_name,
				icon: getVentoraFanSourceIcon(value),
				value: sourceI18nKey ? _format(sourceI18nKey, { default: value }) : value,
			};
		}
		default:
			// Handle presence sensor conditions as fallback
			if (cond_name === 'presence_zone_state' && translated?.add_info_ind) {
				// Zone state condition (presence/absence)
				const zoneId = translated.add_info_ind;
				const isPresence = value === 'true';
				const zoneName = getPresenceZoneName(
					String(zoneId),
					translated?.device?.id,
					get(devices_settings),
				);
				return {
					name: zoneName,
					icon: isPresence ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash',
					value: isPresence ? 'Presence detected' : 'Absence detected',
				};
			} else if (cond_name === 'presence_zone_objects' && translated?.add_info_ind) {
				// Zone object count condition
				const zoneId = translated.add_info_ind;
				const zoneName = getPresenceZoneName(
					String(zoneId),
					translated?.device?.id,
					get(devices_settings),
				);
				return {
					name: zoneName,
					icon: 'fa-solid fa-users',
					value: `Object count: ${value}`,
				};
			}

			return {
				icon: 'fa fa-gear',
				value: '',
			};
	}
};

/**
 * Builds label list (name, icon, value) for conditions that use less-than / more-than style operators.
 * Used for scene condition preview and display (e.g. temperature, meter, scroll_steps).
 *
 * @param {string} cond_name - Condition identifier (e.g. 'scroll_steps', 'temperature', 'meter').
 * @param {string|number} value - Threshold value to display.
 * @param {string} operator - Operator key (e.g. 'more_than', 'less_than').
 * @param {string} [units] - Optional units string for the value (e.g. temperature unit).
 * @param {string} [remote_control_channel] - Optional channel for remote control conditions (e.g. 'channel_2' for scroll_steps); when set and not 'any', appends channel to the label.
 * @returns {{ name: string, icon: string|undefined, value: string }} Object with name, icon, and formatted value string for the label list.
 */
export const less_than_more_than_to_label_list = (
	cond_name,
	value,
	operator,
	units = undefined,
	remote_control_channel = undefined,
) => {
	let icon;
	let iconStyle;

	switch (cond_name) {
		case 'roller position':
		case 'multi_cover_position':
			icon = 'fa fa-blinds-raised';
			break;
		case 'meter':
		case 'total_current':
			icon = 'fa fa-bolt';
			break;
		case 'white_channel':
			icon = 'fa fa-circle';
			break;
		case 'brightness':
			icon = 'fa-light fa-sun-bright';
			break;
		case 'illumination':
		case 'uv':
			icon = 'fa fa-sun';
			break;
		case 'tilt':
			icon = 'fa fa-angle';
			break;
		case 'scroll_steps':
			icon = 'fa fa-arrow-up-arrow-down';
			break;
		case 'temperature':
			icon = 'fa fa-temperature-half';
			break;
		case 'humidity':
		case 'moisture':
		case 'moisture_alarm':
			icon = 'fa fa-droplet-percent';
			break;
		case 'dewpoint':
			icon = 'far fa-droplet-degree';
			break;
		case 'precipitation':
			icon = 'fas fa-raindrops';
			break;
		case 'battery':
			icon = 'fa-duotone fa-light fa-battery-half';
			iconStyle = '--fa-secondary-color: var(--color-3); --fa-secondary-opacity: 1;';
			break;
		case 'dist':
			icon = 'fa fa-ruler-horizontal';
			break;
		case 'percent':
			icon = 'fa fa-percent';
			break;
		case 'vol_meter':
		case 'vol_meter_add_on':
			icon = 'fa fa-circle-v';
			break;
		case 'valve_percent_position':
			icon = 'fa fa-times-circle';
			break;
		case 'trv_target_temperature':
			icon = 'fa fa-crosshairs';
			break;
		case 'output_home_power':
			icon = 'fa fa-house-day';
			break;
		case 'solar_input_power':
			icon = 'fa fa-solar-panel';
			break;
		case 'outputLimit':
			icon = 'fa fa-car-battery';
			break;
		case 'electricLevel':
			icon = 'fa fa-battery-bolt';
			break;
		case 'pressure':
			icon = 'fa-light fa-gauge-high';
			break;
		case 'speed':
		case 'gust_speed':
		case 'direction':
			icon = 'fas fa-wind';
			break;
		case 'adc':
		default:
			if (cond_name === 'presence_zone_objects') {
				console.log(
					'less_than_more_than_to_label_list: Handling presence_zone_objects condition',
				);
				icon = 'fa-solid fa-users';
			} else if (cond_name === 'presence_zone_state') {
				console.log('less_than_more_than_to_label_list: Handling presence_zone_state condition');
				icon = 'fa-solid fa-eye';
			}
			break;
	}

	const _format = get(format);
	let displayValue = `${_format(`operator_${operator.replace(' ', '_')}`)} ${value} ${
		units ? units : condition_to_character(cond_name, undefined)
	}`;

	if (cond_name === 'scroll_steps' && remote_control_channel && remote_control_channel !== 'any') {
		const channelNum = Number(remote_control_channel.split('_')[1]) + 1;
		displayValue = `${displayValue.trimEnd()} ${_format('label_channel', { values: { channel: channelNum } })}`;
	}

	return {
		name: cond_name,
		icon,
		value: displayValue.trim(),
	};
};
